import { create } from 'zustand';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../shared/lib/supabase';

export type UserRole = 'admin' | 'gerente' | 'operador';

export interface AuthUser {
  id: string;
  email: string;
  nome: string;
  role: UserRole;
}

interface AuthState {
  session: Session | null;
  user: AuthUser | null;
  loading: boolean;
  initialized: boolean;
  initialize: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  setSession: (session: Session | null) => Promise<void>;
}

async function loadProfile(user: User | null): Promise<AuthUser | null> {
  if (!user) return null;

  const { data, error } = await supabase
    .from('usuarios')
    .select('id, nome, email, role, ativo')
    .eq('id', user.id)
    .maybeSingle();

  if (error || !data) {
    return {
      id: user.id,
      email: user.email ?? '',
      nome: user.user_metadata?.nome ?? user.email?.split('@')[0] ?? 'Usuário',
      role: 'operador',
    };
  }

  if (!data.ativo) {
    await supabase.auth.signOut();
    throw new Error('Usuário inativo. Contate o administrador.');
  }

  return {
    id: data.id,
    email: data.email,
    nome: data.nome,
    role: data.role as UserRole,
  };
}

export const useAuthStore = create<AuthState>((set, get) => ({
  session: null,
  user: null,
  loading: false,
  initialized: false,

  async initialize() {
    if (get().initialized) return;

    const { data } = await supabase.auth.getSession();
    const user = await loadProfile(data.session?.user ?? null);
    set({ session: data.session, user, initialized: true });

    supabase.auth.onAuthStateChange(async (_event, session) => {
      const nextUser = await loadProfile(session?.user ?? null);
      set({ session, user: nextUser });
    });
  },

  async signIn(email, password) {
    set({ loading: true });
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw new Error(error.message);
      const user = await loadProfile(data.user);
      set({ session: data.session, user });
    } finally {
      set({ loading: false });
    }
  },

  async signOut() {
    await supabase.auth.signOut();
    set({ session: null, user: null });
  },

  async setSession(session) {
    const user = await loadProfile(session?.user ?? null);
    set({ session, user });
  },
}));
